'use client';

import { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Stat {
  id: number;
  value: number;
  suffix: string;
  label: string;
  icon: string;
}

interface StatsSectionProps {
  className?: string;
}

const StatsSection = ({ className = '' }: StatsSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [counts, setCounts] = useState<Record<number, number>>({});
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const stats: Stat[] = [
    {
      id: 1,
      value: 50000,
      suffix: '+',
      label: 'Cards in Use',
      icon: 'UsersIcon'
    },
    {
      id: 2,
      value: 12,
      suffix: '',
      label: 'Cities with Active Users',
      icon: 'MapPinIcon'
    },
    {
      id: 3,
      value: 100,
      suffix: '%',
      label: 'Privacy-Safe Connection',
      icon: 'CheckBadgeIcon'
    },
    {
      id: 4,
      value: 24,
      suffix: '',
      label: 'Always Reachable',
      icon: 'ClockIcon'
    }
  ];

  useEffect(() => {
    if (!isHydrated) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isHydrated]);

  useEffect(() => {
    if (!isVisible || !isHydrated) return;

    stats.forEach((stat) => {
      let current = 0;
      const increment = stat.value / 50;
      const timer = setInterval(() => {
        current += increment;
        if (current >= stat.value) {
          setCounts((prev) => ({ ...prev, [stat.id]: stat.value }));
          clearInterval(timer);
        } else {
          setCounts((prev) => ({ ...prev, [stat.id]: Math.floor(current) }));
        }
      }, 30);
    });
  }, [isVisible, isHydrated]);

  return (
    <section ref={sectionRef} className={`py-20 bg-background ${className}`}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-heading font-extrabold text-foreground mb-4">
            Built for modern urban parking
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Designed to reduce friction, prevent damage, and protect privacy—without changing your routine.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat) => (
            <div
              key={stat.id}
              className="bg-card text-card-foreground rounded-2xl p-8 shadow-subtle border border-border hover:shadow-card hover:border-accent/25 transition-all duration-300 hover:-translate-y-1 text-center"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 bg-accent/10 border border-accent/15 rounded-2xl mb-4 mx-auto">
                <Icon name={stat.icon as any} size={30} className="text-primary" />
              </div>
              <div className="text-4xl lg:text-5xl font-heading font-extrabold text-foreground mb-2">
                {isHydrated ? counts[stat.id] || 0 : stat.value}
                {stat.suffix}
              </div>
              <div className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
